function toWebpUrl(url: string): string | null {
  try {
    const u = new URL(url, window.location.origin);
    if (u.origin !== window.location.origin) return null;
    if (!u.pathname.startsWith('/assets/')) return null;
    if (!/\.(jpe?g|png)$/i.test(u.pathname)) return null;
    u.pathname = u.pathname.replace(/\.(jpe?g|png)$/i, '.webp');
    return u.toString();
  } catch {
    return null;
  }
}

function rewriteSrcsetToWebp(srcset: string): string {
  // Very small srcset parser: replace each URL token's extension if eligible.
  return srcset
    .split(',')
    .map((part) => part.trim())
    .map((part) => {
      if (!part) return part;
      const [url, ...rest] = part.split(/\s+/);
      const webp = toWebpUrl(url);
      return [webp ?? url, ...rest].join(' ');
    })
    .join(', ');
}

export async function enableGlobalWebpImages(): Promise<void> {
  // Optimistic strategy:
  // - Try loading the .webp sibling.
  // - If it fails (browser doesn't support WebP or file missing), fallback to original.

  const upgradeImg = (img: HTMLImageElement) => {
    // If already handled by <picture><source type="image/webp">, skip.
    const parent = img.parentElement;
    if (parent?.tagName.toLowerCase() === 'picture') {
      const source = parent.querySelector('source[type="image/webp"]');
      if (source) return;
    }

    const srcAttr = img.getAttribute('src') || '';
    const current = img.currentSrc || srcAttr;
    const webp = toWebpUrl(current);
    if (!webp) return;

    const original = current;
    if (!img.getAttribute('data-original-src')) img.setAttribute('data-original-src', original);

    const srcset = img.getAttribute('srcset');
    if (srcset && !img.getAttribute('data-original-srcset')) {
      img.setAttribute('data-original-srcset', srcset);
      img.setAttribute('srcset', rewriteSrcsetToWebp(srcset));
    }

    img.addEventListener(
      'error',
      () => {
        const origSrc = img.getAttribute('data-original-src');
        if (origSrc) img.src = origSrc;
        const origSrcset = img.getAttribute('data-original-srcset');
        if (origSrcset) img.setAttribute('srcset', origSrcset);
      },
      { once: true }
    );

    // Avoid re-setting if already webp
    if (/\.webp(\?|#|$)/i.test(srcAttr)) return;

    img.src = webp;
  };

  const upgradeInlineStyle = (el: HTMLElement) => {
    const style = el.getAttribute('style');
    if (!style || !style.includes('url(')) return;
    const replaced = style.replace(/url\((['"]?)([^'")]+)\1\)/gi, (m, q, url) => {
      const abs = (() => {
        try {
          return new URL(url, window.location.origin).toString();
        } catch {
          return null;
        }
      })();
      const webp = abs ? toWebpUrl(abs) : null;
      if (!webp) return m;
      return `url(${q}${webp}${q})`;
    });
    if (replaced !== style) el.setAttribute('style', replaced);
  };

  const upgradeTree = (root: ParentNode) => {
    root.querySelectorAll?.('img')?.forEach((n) => upgradeImg(n as HTMLImageElement));
    root.querySelectorAll?.('[style*="url("]')?.forEach((n) => upgradeInlineStyle(n as HTMLElement));
  };

  // Initial pass
  upgradeTree(document);

  // Observe future DOM injections (blog/articles/modals)
  const w = window as any;
  if (w.__cryptoaccounting_webp_observer) return;
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'childList') {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.tagName.toLowerCase() === 'img') upgradeImg(node as HTMLImageElement);
          upgradeTree(node);
        });
      } else if (m.type === 'attributes' && m.target instanceof HTMLElement) {
        const el = m.target;
        if (el.tagName.toLowerCase() === 'img') upgradeImg(el as HTMLImageElement);
        if (m.attributeName === 'style') upgradeInlineStyle(el);
      }
    }
  });
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['src', 'srcset', 'style']
  });
  w.__cryptoaccounting_webp_observer = observer;
}
